import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Text,
  Paper,
  UnstyledButton,
  ActionIcon,
  Modal,
  Stack
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { apiClient } from '../services/api';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { Game, Player } from '../types/models';
import { GameLayout } from './layouts/GameLayout';

interface AvatarHistoryEntry {
  id: string;
  avatarUrl: string;
  avatarThumbUrl?: string | null;
  createdAt: string;
  source?: string;
}

interface LobbyAvatarHistoryProps {
  game: Game;
  currentPlayer: Player;
  onCancel: () => void;
  onApplied: () => void;
}

const LobbyAvatarHistory: React.FC<LobbyAvatarHistoryProps> = ({
  game,
  currentPlayer,
  onCancel,
  onApplied
}) => {
  const { isMobile } = useResponsiveLayout();
  const [avatars, setAvatars] = useState<AvatarHistoryEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AvatarHistoryEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedAvatar = useMemo(
    () => avatars.find((avatar) => avatar.id === selectedId) || null,
    [avatars, selectedId]
  );

  useEffect(() => {
    let isActive = true;
    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.getAvatarHistory(game.key, currentPlayer.id);
        if (!isActive) return;
        setAvatars(response.data.avatars || []);
      } catch (err) {
        if (!isActive) return;
        setError('Failed to load avatar history.');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isActive = false;
    };
  }, [game.key, currentPlayer.id]);

  const handleUseAvatar = async () => {
    if (!selectedAvatar) return;
    setIsApplying(true);
    setError(null);
    try {
      await apiClient.updatePlayer(game.key, currentPlayer.id, {
        avatarUrl: selectedAvatar.avatarUrl,
        avatarThumbUrl: selectedAvatar.avatarThumbUrl || null
      });
      localStorage.setItem('userAvatarUrl', selectedAvatar.avatarUrl);
      if (selectedAvatar.avatarThumbUrl) {
        localStorage.setItem('userAvatarThumbUrl', selectedAvatar.avatarThumbUrl);
      } else {
        localStorage.removeItem('userAvatarThumbUrl');
      }
      localStorage.removeItem('userAvatarDataUrl');
      onApplied();
    } catch (err) {
      setError('Failed to update avatar. Please try again.');
      setIsApplying(false);
    }
  };

  const handleDeleteAvatar = async (historyId: string) => {
    setDeletingId(historyId);
    setError(null);
    try {
      await apiClient.deleteAvatarHistory(game.key, currentPlayer.id, historyId);
      setAvatars((prev) => prev.filter((avatar) => avatar.id !== historyId));
      setSelectedId((prev) => (prev === historyId ? null : prev));
    } catch (err) {
      setError('Failed to delete avatar. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const header = (
    <Group gap="sm" justify="space-between" align="center" wrap="nowrap">
      <Button
        variant="light"
        size={isMobile ? 'sm' : 'md'}
        onClick={onCancel}
        disabled={isApplying}
        style={{ flexShrink: 0 }}
      >
        Cancel
      </Button>
      <Text fw={600} size={isMobile ? 'sm' : 'md'} ta="center" style={{ flex: 1 }}>
        Choose a previous avatar
      </Text>
      <Button
        size={isMobile ? 'sm' : 'md'}
        onClick={handleUseAvatar}
        loading={isApplying}
        disabled={!selectedAvatar}
        style={{ flexShrink: 0 }}
      >
        Use this
      </Button>
    </Group>
  );

  const content = (
    <Box style={{ width: '100%', maxWidth: 900 }}>
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : avatars.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No previous avatars yet.</Text>
        </Center>
      ) : (
        <SimpleGrid cols={isMobile ? 2 : 4} spacing="md">
          {avatars.map((avatar) => {
            const isSelected = avatar.id === selectedId;
            return (
              <UnstyledButton
                key={avatar.id}
                onClick={() => setSelectedId(avatar.id)}
                style={{ width: '100%' }}
              >
                <Paper
                  radius="md"
                  withBorder
                  style={{
                    borderColor: isSelected ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-gray-3)',
                    borderWidth: isSelected ? 2 : 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  <ActionIcon
                    variant="light"
                    color="red"
                    size="sm"
                    loading={deletingId === avatar.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingDelete(avatar);
                    }}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2
                    }}
                    aria-label="Delete avatar"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                  <Box
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={avatar.avatarThumbUrl || avatar.avatarUrl}
                      alt="Avatar history"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                </Paper>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>
      )}
      {error && (
        <Text c="red" size="sm" ta="center" mt="sm">
          {error}
        </Text>
      )}
    </Box>
  );

  return (
    <>
      <GameLayout
        mode="draw"
        header={header}
        content={content}
      />
      <Modal
        opened={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Delete this avatar?"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text>Delete this avatar from your history? This can't be undone.</Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (pendingDelete) {
                  handleDeleteAvatar(pendingDelete.id);
                }
                setPendingDelete(null);
              }}
              loading={pendingDelete ? deletingId === pendingDelete.id : false}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default LobbyAvatarHistory;
